import React, { FC } from 'react';
import L, { LeafletMouseEvent } from 'leaflet';
import { useMap } from 'react-leaflet';
import { useNetworkElementsStore } from '../../store/networkElementsStore';
import { useNetworkUIStore } from '../../store/networkUIStore';
import { Abgangsleitung, AnyNetworkElement, Hausanschluss } from '../../types/network';
import { NetworkElementType } from '../../types/network';
import { NetworkMarker } from './markers/NetworkMarker';
import { AbgangsleitungLine } from './lines/AbgangsleitungLine';
import { selectWithCoLocation } from './elementHitTest';
import { sortOverlapGroup } from './markers/shared/coLocationOffset';
import { useCouplingStore } from '../../store/couplingStore';

const NetworkElements: FC = React.memo(() => {
  // Shared SVG renderer for animated overlays (CSS animations need SVG, not Canvas)
  const svgRenderer = React.useMemo(() => L.svg(), []);

  // Optimized store selectors - only subscribe to specific data we need
  const elements = useNetworkElementsStore(state => state.elements);
  const powerMeasurements = useNetworkElementsStore(state => state.powerMeasurements);
  const visibleElementTypes = useNetworkUIStore(state => state.visibleElementTypes);
  const selectedElementId = useNetworkUIStore(state => state.selectedElement?.id);
  const highlightedElementId = useNetworkUIStore(state => state.highlightedElementId);
  // Kuppelpunkt-Muffen exist only to close the zone topologically at a foreign KVS/ONS
  // (NETZVERBUND.md 6): physically the cable enters that cabinet. They are rendered as the
  // Trennstellen marker, never as a sleeve — a sleeve symbol there suggested a real Muffe.
  const couplings = useCouplingStore(state => state.couplings);
  const kuppelpunktIds = React.useMemo(() => new Set(couplings.map(c => c.endpointElementId)), [couplings]);

  // Optimized coordinate map with better performance
  const elementsByCoordinates = React.useMemo(() => {
    const coordMap = new Map<string, AnyNetworkElement[]>();

    // Process only non-abgangsleitung elements with coordinates
    const validElements = elements.filter(
      element => element.type !== 'abgangsleitung' && 'coordinates' in element && !kuppelpunktIds.has(element.id)
    );

    for (const element of validElements) {
      const key = `${element.coordinates.lat},${element.coordinates.lng}`;
      const existing = coordMap.get(key);

      if (existing) {
        existing.push(element);
      } else {
        coordMap.set(key, [element]);
      }
    }

    for (const [key, group] of coordMap) {
      if (group.length > 1) coordMap.set(key, sortOverlapGroup(group));
    }
    return coordMap;
  }, [elements, kuppelpunktIds]);

  const map = useMap();

  // Throttle the SVG power-flow animation by cable count. map-performance.css defines
  // .cable-count-high (slower) / .cable-count-very-high (off) - the classes were never
  // set, so a 500-cable zone animated every powered segment at 30 fps forever.
  const cableCount = React.useMemo(
    () => elements.reduce((n, el) => n + (el.type === 'abgangsleitung' ? Math.max(1, (el as Abgangsleitung).segments?.length ?? 1) : 0), 0),
    [elements]
  );
  React.useEffect(() => {
    const container = map.getContainer();
    container.classList.toggle('cable-count-high', cableCount > 250 && cableCount <= 600);
    container.classList.toggle('cable-count-very-high', cableCount > 600);
  }, [map, cableCount]);

  const handleMarkerClick = React.useCallback((element: AnyNetworkElement) => (e: LeafletMouseEvent) => {
    e.originalEvent.preventDefault();
    e.originalEvent.stopPropagation();
    L.DomEvent.stopPropagation(e);
    // Selects the clicked marker and records everything else under the cursor
    // (stacked markers, trench cables) for the details panel.
    selectWithCoLocation(map, e, element);
  }, [map]);

  // Optimized filtering with early returns and better memoization
  const { visibleLines, visibleMarkers } = React.useMemo(() => {
    const lines: Abgangsleitung[] = [];
    const markers: AnyNetworkElement[] = [];

    // Single pass through elements for better performance
    for (const element of elements) {
      if (!visibleElementTypes.has(element.type as NetworkElementType)) {
        continue;
      }

      if (element.type === 'abgangsleitung') {
        lines.push(element as Abgangsleitung);
      } else if (!kuppelpunktIds.has(element.id)) {
        markers.push(element);
      }
    }

    return { visibleLines: lines, visibleMarkers: markers };
  }, [elements, visibleElementTypes, kuppelpunktIds]);

  // Optimized marker processing with reduced calculations
  const processedMarkers = React.useMemo(() => {
    return visibleMarkers.map(element => {
      if ('coordinates' in element) {
        const key = `${element.coordinates.lat},${element.coordinates.lng}`;
        const overlappingElements = elementsByCoordinates.get(key) || [];
        const offsetIndex = overlappingElements.findIndex(e => e.id === element.id);
        const shouldOffset = overlappingElements.length > 1;

        // Get power measurements for this element
        const powerMeasurement = powerMeasurements[element.id];

        // Get simulation data if it's a hausanschluss (optimized check)
        let simulatedLoadPercentage: number | undefined;
        let connectionPower: number | undefined;
        if (element.type === 'hausanschluss') {
          const hausanschluss = element as Hausanschluss;
          simulatedLoadPercentage = hausanschluss.simulatedPowerPercent ?? undefined;
          connectionPower = hausanschluss.connectionPower;
        }

        return {
          element,
          offsetIndex: shouldOffset ? offsetIndex : undefined,
          simulatedLoadPercentage,
          powerMeasurement,
          connectionPower
        };
      }
      return {
        element,
        offsetIndex: undefined,
        simulatedLoadPercentage: undefined,
        powerMeasurement: undefined,
        connectionPower: undefined
      };
    });
  }, [visibleMarkers, elementsByCoordinates, powerMeasurements]);

  // Memoized line components to prevent unnecessary re-renders
  const lineComponents = React.useMemo(() => {
    return visibleLines.map(element => (
      <AbgangsleitungLine
        key={element.id}
        element={element}
        isHighlighted={element.id === highlightedElementId}
        svgRenderer={svgRenderer}
      />
    ));
  }, [visibleLines, highlightedElementId, svgRenderer]);

  // Memoized marker components to prevent unnecessary re-renders
  const markerComponents = React.useMemo(() => {
    return processedMarkers.map(({ element, offsetIndex, simulatedLoadPercentage, powerMeasurement, connectionPower }) => (
      <NetworkMarker
        key={element.id}
        element={element}
        isSelected={selectedElementId === element.id}
        isHighlighted={element.id === highlightedElementId}
        onClick={handleMarkerClick(element)}
        offsetIndex={offsetIndex}
        simulatedLoadPercentage={simulatedLoadPercentage}
        powerMeasurement={powerMeasurement}
        connectionPower={connectionPower}
      />
    ));
  }, [processedMarkers, selectedElementId, highlightedElementId, handleMarkerClick]);

  return (
    <>
      {lineComponents}
      {markerComponents}
    </>
  );
});

export default NetworkElements;
